// /ad-strategist-v1 — Ad Strategist V1 (Option B, Jul 23 2026).
//
// Full 4-step AS flow, powered entirely by the AS-owned /api/ad-strategist-v1/*
// backend (routes/ad_strategist_v1.py). The old /ad-strategist page + "Generate
// 20 more" are untouched.
//   Landing — list previous analyses; open one (regen single ads) or start new.
//   Step 1  — Pull fresh competitor ads (top 4 per competitor).
//   Step 2  — Strategic angles + a vision-grounded "stand out visually" finding.
//   Step 3  — Pick products → build 20 concepts.
//   Step 4  — Winning ideas grid: tiles blank; per-card Generate + Generate All,
//             each via the FIXED Cloud Tasks render (slotted, no hang).

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayoutV2 from '@/components/feature/AppLayoutV2';
import AccordionStep, { type SectionState } from '@/pages/appv2/components/Section';
import ProductCatalogPicker, {
  type ProductPickerSelection,
} from '@/components/feature/ProductCatalogPicker';
import { useCredits } from '@/hooks/useCredits';
import {
  adStrategistV1Run,
  adStrategistV1Build20Concepts,
  listAdStrategistV1Jobs,
  getAdStrategistV1Job,
  getAdStrategistV1JobRenders,
  renderAdStrategistV1Concept,
  editAdStrategistV1Concept,
  getBrand,
  listCompetitors,
  listProducts,
  updateAdStrategistJob,
  deleteAdStrategistJob,
  type AdBrief,
  type AdvertiserBlock,
  type AdStrategistJob,
  type AdStrategist20Idea,
  type AdStrategist20Concept,
  type GatheredAd,
  type UserProduct,
} from '@/lib/api';

const POLL_MS = 4000;
const MAX_ADS_SHOWN = 12; // ads are ranked most-successful-first; show up to this many per competitor

const HOOK_CHIP: Record<string, { chip: string; hex: string }> = {
  demonstration: { chip: 'bg-sky-100 text-sky-800', hex: '#0284c7' },
  'before-after': { chip: 'bg-emerald-100 text-emerald-800', hex: '#059669' },
  'social-proof': { chip: 'bg-amber-100 text-amber-800', hex: '#d97706' },
  testimonial: { chip: 'bg-rose-100 text-rose-800', hex: '#e11d48' },
  comparison: { chip: 'bg-violet-100 text-violet-800', hex: '#7c3aed' },
  lifestyle: { chip: 'bg-orange-100 text-orange-800', hex: '#ea580c' },
  'ingredient-focus': { chip: 'bg-lime-100 text-lime-800', hex: '#65a30d' },
  'problem-agitation': { chip: 'bg-red-100 text-red-800', hex: '#dc2626' },
  'founder-story': { chip: 'bg-indigo-100 text-indigo-800', hex: '#4f46e5' },
  ritual: { chip: 'bg-pink-100 text-pink-800', hex: '#db2777' },
  'behind-scenes': { chip: 'bg-slate-100 text-slate-800', hex: '#475569' },
  statistic: { chip: 'bg-cyan-100 text-cyan-800', hex: '#0891b2' },
};
const DEFAULT_CHIP = { chip: 'bg-surface-dark/30 text-dark-muted', hex: '#64748b' };
const hookChip = (h: string) => HOOK_CHIP[h] ?? DEFAULT_CHIP;

// Compact FB / IG surface badge (matches old AS). Null when unclassified.
function platformBadge(platforms?: string[]): { label: string; bg: string; title: string } | null {
  if (!platforms || platforms.length === 0) return null;
  const u = platforms.map((p) => p.toUpperCase());
  const fb = u.includes('FACEBOOK');
  const ig = u.includes('INSTAGRAM');
  if (fb && ig)
    return {
      label: 'FB · IG',
      bg: 'bg-gradient-to-r from-[#1877F2] to-[#E1306C]',
      title: 'Runs on both Facebook and Instagram',
    };
  if (ig)
    return {
      label: 'IG',
      bg: 'bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF]',
      title: 'Instagram',
    };
  if (fb) return { label: 'FB', bg: 'bg-[#1877F2]', title: 'Facebook' };
  return null;
}

// Format + longevity chips under each thumbnail (matches old AS). Longevity is
// the strongest "this is working" signal, so it gets the emerald treatment.
function AdMetaChips({ ad }: { ad: GatheredAd }) {
  const showFormat = ad.format && ad.format !== 'unknown';
  const showLongevity = ad.longevity_days != null && ad.longevity_days > 0;
  if (!showFormat && !showLongevity) return null;
  const formatIcon =
    ad.format === 'video'
      ? 'ri-video-line'
      : ad.format === 'carousel'
        ? 'ri-stack-line'
        : ad.format === 'dynamic'
          ? 'ri-apps-2-line'
          : 'ri-image-line';
  return (
    <div className="px-1.5 py-1 flex flex-wrap items-center gap-1">
      {showFormat && (
        <span
          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-surface text-dark-muted text-[9px] font-bold border border-surface-dark/30 leading-none"
          title={`Format: ${ad.format}`}
        >
          <i className={`${formatIcon} text-[10px]`} />
          {ad.format}
        </span>
      )}
      {showLongevity && (
        <span
          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[9px] font-bold border border-emerald-100 leading-none"
          title={`Running ${ad.longevity_days} days — long-running = working`}
        >
          <i className="ri-timer-line text-[10px]" />
          {ad.longevity_days}d
        </span>
      )}
    </div>
  );
}

// One competitor's ads as a compact horizontal strip of small tiles (matches
// old AS size + information). Prefers our cached thumbnail; falls back to the
// raw media ref.
function CompetitorAdStrip({
  block,
  onOpenAd,
}: {
  block: AdvertiserBlock;
  onOpenAd: (ad: GatheredAd) => void;
}) {
  const ads = (block.ads || [])
    .filter((a) => a.media_cached_url || a.media_ref)
    .slice(0, MAX_ADS_SHOWN);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      setCanLeft(scrollLeft > 8);
      setCanRight(scrollLeft + clientWidth < scrollWidth - 8);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, [ads.length]);

  const TILE_W = 128; // w-32
  const TILE_GAP = 8; // gap-2
  const scrollByOne = (dir: 1 | -1) =>
    scrollRef.current?.scrollBy({ left: dir * (TILE_W + TILE_GAP), behavior: 'smooth' });

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-bold uppercase tracking-wide text-dark-muted">{block.label}</p>
        <span className="text-xs text-dark-muted">
          {ads.length} ad{ads.length === 1 ? '' : 's'}
        </span>
      </div>
      <div className="relative">
        {canLeft && (
          <button
            type="button"
            onClick={() => scrollByOne(-1)}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white/95 border border-surface-dark/40 text-dark hover:text-primary hover:border-primary shadow-md shadow-black/10 flex items-center justify-center cursor-pointer transition-colors"
            aria-label="Scroll left"
          >
            <i className="ri-arrow-left-s-line text-lg" />
          </button>
        )}
        {canRight && (
          <button
            type="button"
            onClick={() => scrollByOne(1)}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white/95 border border-surface-dark/40 text-dark hover:text-primary hover:border-primary shadow-md shadow-black/10 flex items-center justify-center cursor-pointer transition-colors"
            aria-label="Scroll right"
          >
            <i className="ri-arrow-right-s-line text-lg" />
          </button>
        )}
        <div
          ref={scrollRef}
          className="flex gap-2 overflow-x-auto snap-x scroll-smooth pb-1 -mb-1 [&::-webkit-scrollbar]:hidden [scrollbar-width:none]"
        >
          {ads.map((ad, i) => {
            const badge = platformBadge(ad.platforms);
            return (
              <button
                type="button"
                key={i}
                onClick={() => onOpenAd(ad)}
                className="group flex-shrink-0 w-32 snap-start flex flex-col rounded-lg overflow-hidden bg-white border border-surface-dark/25 cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-md hover:shadow-primary/15 hover:border-primary/30 text-left"
                title={ad.ad_text || ad.headline || 'View ad detail'}
              >
                <div className="relative aspect-square bg-surface-dark/20">
                  <img
                    src={(ad.media_cached_url || ad.media_ref) as string}
                    alt={ad.headline || `ad ${i + 1}`}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                  />
                  {badge && (
                    <span
                      className={`absolute top-1 left-1 px-1.5 py-0.5 rounded text-[9px] font-bold leading-none text-white ${badge.bg}`}
                      title={badge.title}
                    >
                      {badge.label}
                    </span>
                  )}
                  <span className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/55 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <i className="ri-zoom-in-line text-xs" />
                  </span>
                </div>
                <AdMetaChips ad={ad} />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Full-detail modal for one competitor ad (matches old AS): big creative +
// every metadata field + deep-link to the original on the Meta Ad Library.
function CompetitorAdModal({
  ad,
  advertiser,
  onClose,
}: {
  ad: GatheredAd;
  advertiser: string;
  onClose: () => void;
}) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const adLibraryUrl = ad.ad_archive_id
    ? `https://www.facebook.com/ads/library/?id=${encodeURIComponent(ad.ad_archive_id)}`
    : null;
  const badge = platformBadge(ad.platforms);
  const img = ad.media_cached_url || ad.media_ref;
  const formatIcon =
    ad.format === 'video'
      ? 'ri-video-line'
      : ad.format === 'carousel'
        ? 'ri-stack-line'
        : ad.format === 'dynamic'
          ? 'ri-apps-2-line'
          : 'ri-image-line';
  const fmtDate = (iso?: string) =>
    iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Ad from ${advertiser}`}
    >
      <div
        className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl bg-white shadow-2xl shadow-primary/15"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/90 border border-surface-dark/30 text-dark-muted hover:text-dark hover:bg-white shadow-sm flex items-center justify-center cursor-pointer transition-colors"
          aria-label="Close"
        >
          <i className="ri-close-line text-lg" />
        </button>

        <div className="px-6 pt-5 pb-3 border-b border-surface-dark/20">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
              <i className="ri-sword-line text-lg" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-dark-muted uppercase tracking-wide">Competitor ad</p>
              <h3 className="font-display font-bold text-dark text-base truncate">{advertiser}</h3>
            </div>
            {badge && (
              <span
                className={`flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-bold text-white ${badge.bg}`}
                title={badge.title}
              >
                {badge.label}
              </span>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-[1fr_1fr]">
          <div className="bg-surface-dark/10 p-3 md:p-4 flex items-center justify-center">
            {img ? (
              <img
                src={img}
                alt={ad.headline || 'Ad creative'}
                className="max-w-full max-h-[42vh] object-contain rounded-lg shadow-md shadow-black/10"
              />
            ) : (
              <div className="py-12 text-center text-dark-muted text-sm">
                <i className="ri-image-line text-3xl" />
                <p className="mt-2">No image cached for this ad</p>
              </div>
            )}
          </div>

          <div className="p-5 md:p-6 space-y-4">
            {ad.headline && (
              <div>
                <p className="text-[10px] font-bold text-dark-muted uppercase tracking-wide mb-1">Headline</p>
                <p className="font-display font-bold text-dark text-base leading-snug">{ad.headline}</p>
              </div>
            )}
            {ad.ad_text && (
              <div>
                <p className="text-[10px] font-bold text-dark-muted uppercase tracking-wide mb-1">Ad copy</p>
                <p className="text-sm text-dark leading-relaxed whitespace-pre-wrap">{ad.ad_text}</p>
              </div>
            )}
            {ad.link_desc && (
              <div>
                <p className="text-[10px] font-bold text-dark-muted uppercase tracking-wide mb-1">Link description</p>
                <p className="text-sm text-dark-muted leading-relaxed">{ad.link_desc}</p>
              </div>
            )}

            <div className="flex flex-wrap gap-1.5 pt-1">
              {ad.cta && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold">
                  <i className="ri-cursor-line text-xs" /> {ad.cta}
                </span>
              )}
              {ad.format && ad.format !== 'unknown' && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-surface text-dark-muted text-xs font-semibold border border-surface-dark/30">
                  <i className={`text-xs ${formatIcon}`} /> {ad.format}
                </span>
              )}
              {ad.longevity_days != null && ad.longevity_days > 0 && (
                <span
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold border border-emerald-100"
                  title="Longevity = how many days this ad has been running. Long-running ads usually mean it's working."
                >
                  <i className="ri-timer-line text-xs" /> Running {ad.longevity_days}d
                </span>
              )}
              {ad.variations != null && ad.variations > 1 && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-surface text-dark-muted text-xs font-semibold border border-surface-dark/30">
                  <i className="ri-file-copy-line text-xs" /> {ad.variations} variations
                </span>
              )}
            </div>

            {(ad.first_shown || ad.last_shown) && (
              <p className="text-[11px] text-dark-muted pt-1">
                {fmtDate(ad.first_shown) && <>First shown: {fmtDate(ad.first_shown)}</>}
                {fmtDate(ad.first_shown) && fmtDate(ad.last_shown) && <> · </>}
                {fmtDate(ad.last_shown) && <>Last shown: {fmtDate(ad.last_shown)}</>}
              </p>
            )}

            {(adLibraryUrl || ad.landing_url) && (
              <div className="flex flex-wrap gap-2 pt-2 border-t border-surface-dark/20">
                {adLibraryUrl && (
                  <a
                    href={adLibraryUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary hover:bg-primary-hover text-white text-xs font-bold transition-colors cursor-pointer"
                  >
                    <i className="ri-external-link-line" /> View on Meta Ad Library
                  </a>
                )}
                {ad.landing_url && (
                  <a
                    href={ad.landing_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-surface-dark/40 hover:border-primary hover:text-primary text-dark-muted text-xs font-bold transition-colors cursor-pointer"
                  >
                    <i className="ri-link" /> Visit landing page
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

type SlotState =
  | { status: 'idle' }
  | { status: 'pending' }
  | { status: 'queued'; postId: string }
  | { status: 'rendered'; postId: string; imageUrl: string | null };

const key = (ii: number, ci: number) => `${ii}_${ci}`;

function runLabel(run: AdStrategistJob): string {
  if (run.name && run.name.trim()) return run.name.trim();
  const angles = run.angle_names ?? [];
  if (angles.length) {
    const head = angles.slice(0, 2).join(', ');
    return angles.length > 2 ? `${head} & ${angles.length - 2} more` : head;
  }
  return 'Competitive analysis';
}

function fmtDate(iso?: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

export default function AdStrategistV1Page() {
  const navigate = useNavigate();

  // Tablet + desktop only — see the isMobile gate before the render returns.
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 768,
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const { balance, refresh: refreshCredits } = useCredits();

  const [view, setView] = useState<'list' | 'analysis'>('list');
  const [error, setError] = useState<string | null>(null);

  // Landing — saved runs
  const [runs, setRuns] = useState<AdStrategistJob[] | null>(null);
  const [openingRunId, setOpeningRunId] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [rowBusyId, setRowBusyId] = useState<string | null>(null);

  // id → product, for showing the featured product on each winning-idea tile
  // (concepts carry recommended_product_id). Fetched once; products rarely
  // change mid-session.
  const [productsById, setProductsById] = useState<Map<string, UserProduct>>(new Map());

  // Step 1 — run
  const [jobId, setJobId] = useState<string | null>(null);
  const [brief, setBrief] = useState<AdBrief | null>(null);
  const [competitorBlocks, setCompetitorBlocks] = useState<AdvertiserBlock[]>([]);
  const [running, setRunning] = useState(false);
  const [openAd, setOpenAd] = useState<{ ad: GatheredAd; advertiser: string } | null>(null);

  // Step 3 — products
  const [selection, setSelection] = useState<ProductPickerSelection | null>(null);
  const [buildingConcepts, setBuildingConcepts] = useState(false);

  // Step 4 — grid
  const [activeStep, setActiveStep] = useState(1);
  const [ideas, setIdeas] = useState<AdStrategist20Idea[] | null>(null);
  const [slots, setSlots] = useState<Record<string, SlotState>>({});
  const [generatingAll, setGeneratingAll] = useState(false);

  // ── Landing: load saved runs on mount (NO auto-run). ──
  const loadRuns = useCallback(async () => {
    try {
      const list = await listAdStrategistV1Jobs();
      setRuns(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load previous analyses.');
      setRuns([]);
    }
  }, []);

  useEffect(() => {
    void loadRuns();
    void listProducts()
      .then((list) => setProductsById(new Map(list.map((p) => [p.id, p]))))
      .catch(() => {
        /* tiles fall back to name-only */
      });
  }, [loadRuns]);

  const resetAnalysisState = () => {
    setJobId(null);
    setBrief(null);
    setCompetitorBlocks([]);
    setSelection(null);
    setIdeas(null);
    setSlots({});
    setActiveStep(1);
  };

  // ── Start a NEW analysis (Step 1 auto-run). ──
  const startNewAnalysis = useCallback(async () => {
    setError(null);
    resetAnalysisState();
    setView('analysis');
    setRunning(true);
    try {
      const [brand, comps] = await Promise.all([
        getBrand().catch(() => null),
        listCompetitors().catch(() => ({ competitors: [] as any[] })),
      ]);
      const competitors = ((comps as any)?.competitors || []).map((c: any) => ({
        query: c.query,
        label: c.label || c.name || null,
      }));
      const b = brand as any;
      const job = await adStrategistV1Run({
        brandName: b?.name || undefined,
        brandDescription: (b?.description || b?.brief) || undefined,
        brandWebsite: (b?.sourceUrl || b?.website) || undefined,
        competitors,
      });
      setJobId(job.id);
      const res: any = job.result;
      setBrief(res?.brief || null);
      setCompetitorBlocks(res?.gathered?.competitors || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not pull competitor ads.');
    } finally {
      setRunning(false);
    }
  }, []);

  // ── Open a saved run (view brief + regen single ads). ──
  const openRun = useCallback(async (id: string) => {
    setError(null);
    setOpeningRunId(id);
    try {
      const job = await getAdStrategistV1Job(id);
      const res: any = job.result;
      resetAnalysisState();
      setJobId(job.id);
      setBrief(res?.brief || null);
      setCompetitorBlocks(res?.gathered?.competitors || []);
      const runIdeas: AdStrategist20Idea[] | null = res?.brief?.ideas || null;
      setIdeas(runIdeas);
      setRunning(false);
      setView('analysis');
      setActiveStep(runIdeas && runIdeas.length ? 4 : res?.brief ? 2 : 1);
      if (runIdeas && runIdeas.length) {
        try {
          const rr = await getAdStrategistV1JobRenders(id);
          const next: Record<string, SlotState> = {};
          for (const r of rr.renders) {
            if (r.idea_idx < 0 || r.concept_idx < 0) continue;
            next[key(r.idea_idx, r.concept_idx)] =
              r.status === 'rendered'
                ? { status: 'rendered', postId: r.post_id, imageUrl: r.image_url }
                : { status: 'queued', postId: r.post_id };
          }
          setSlots(next);
        } catch {
          /* tiles hydrate on next poll */
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not open that analysis.');
    } finally {
      setOpeningRunId(null);
    }
  }, []);

  const backToList = useCallback(() => {
    setView('list');
    setError(null);
    void loadRuns();
  }, [loadRuns]);

  const saveRename = useCallback(
    async (id: string) => {
      const name = renameValue.trim();
      setRowBusyId(id);
      try {
        await updateAdStrategistJob(id, { name: name || undefined });
        setRuns((prev) => (prev ? prev.map((r) => (r.id === id ? { ...r, name: name || null } : r)) : prev));
        setRenamingId(null);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Rename failed.');
      } finally {
        setRowBusyId(null);
      }
    },
    [renameValue],
  );

  const deleteRun = useCallback(async (id: string) => {
    if (!window.confirm('Delete this analysis? This cannot be undone.')) return;
    setRowBusyId(id);
    try {
      await deleteAdStrategistJob(id);
      setRuns((prev) => (prev ? prev.filter((r) => r.id !== id) : prev));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed.');
    } finally {
      setRowBusyId(null);
    }
  }, []);

  // ── Step 3 → build 20 concepts. ──
  const buildConcepts = useCallback(async () => {
    if (!jobId || buildingConcepts) return;
    const ids = selection?.allSelected ? [] : selection?.selectedIds ?? [];
    if (!selection?.allSelected && ids.length === 0) {
      setError('Pick at least one product to feature.');
      return;
    }
    setBuildingConcepts(true);
    setError(null);
    try {
      const res = await adStrategistV1Build20Concepts({ jobId, productIds: ids });
      setIdeas(res.ideas);
      setSlots({});
      setActiveStep(4);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not build winning ideas.');
    } finally {
      setBuildingConcepts(false);
    }
  }, [jobId, selection, buildingConcepts]);

  // ── Per-card render. ──
  const renderOne = useCallback(
    async (ii: number, ci: number) => {
      if (!jobId) return;
      const k = key(ii, ci);
      setSlots((s) => (s[k]?.status === 'rendered' ? s : { ...s, [k]: { status: 'pending' } }));
      try {
        const res = await renderAdStrategistV1Concept({ jobId, ideaIdx: ii, conceptIdx: ci });
        setSlots((s) => ({
          ...s,
          [k]:
            res.status === 'rendered'
              ? { status: 'rendered', postId: res.post_id, imageUrl: null }
              : { status: 'queued', postId: res.post_id },
        }));
        void refreshCredits();
      } catch (e) {
        setSlots((s) => ({ ...s, [k]: { status: 'idle' } }));
        setError(e instanceof Error ? e.message : 'Render failed.');
      }
    },
    [jobId, refreshCredits],
  );

  // ── Inline edit of a concept's copy (headline/subhead). Optimistic local
  // update, then persist to the stored job so the render re-derives the edited
  // text. FREE (no credits). Reverts on failure. ──
  const editConcept = useCallback(
    async (ii: number, ci: number, headline: string, subhead: string) => {
      if (!jobId || !ideas) return;
      const prevSnapshot = ideas;
      setIdeas((cur) =>
        !cur
          ? cur
          : cur.map((idea, i) =>
              i !== ii
                ? idea
                : {
                    ...idea,
                    concepts: (idea.concepts || []).map((c, j) =>
                      j !== ci ? c : { ...c, headline, subhead },
                    ),
                  },
            ),
      );
      try {
        await editAdStrategistV1Concept({ jobId, ideaIdx: ii, conceptIdx: ci, headline, subhead });
      } catch (e) {
        setIdeas(prevSnapshot);
        setError(e instanceof Error ? e.message : 'Could not save your edit.');
      }
    },
    [jobId, ideas],
  );

  const generateAll = useCallback(async () => {
    if (!ideas || generatingAll) return;
    setGeneratingAll(true);
    const jobs: Promise<void>[] = [];
    ideas.forEach((idea, ii) =>
      (idea.concepts || []).forEach((_, ci) => {
        if (slots[key(ii, ci)]?.status === 'rendered') return;
        jobs.push(renderOne(ii, ci));
      }),
    );
    await Promise.allSettled(jobs);
    setGeneratingAll(false);
  }, [ideas, slots, generatingAll, renderOne]);

  // ── Poll renders while on step 4 to fill tiles as Cloud Tasks complete. ──
  useEffect(() => {
    if (view !== 'analysis' || activeStep !== 4 || !jobId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const rr = await getAdStrategistV1JobRenders(jobId);
        if (cancelled) return;
        setSlots((prev) => {
          const next = { ...prev };
          for (const r of rr.renders) {
            if (r.idea_idx < 0 || r.concept_idx < 0) continue;
            const k = key(r.idea_idx, r.concept_idx);
            if (r.status === 'rendered') {
              next[k] = { status: 'rendered', postId: r.post_id, imageUrl: r.image_url };
            } else if (!next[k] || next[k].status === 'idle') {
              next[k] = { status: 'queued', postId: r.post_id };
            }
          }
          return next;
        });
      } catch {
        /* transient — keep polling */
      }
    };
    void tick();
    const interval = setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [view, activeStep, jobId]);

  const stepState = (n: number): SectionState =>
    n < activeStep ? 'completed' : n === activeStep ? 'active' : 'locked';

  const totalAds = competitorBlocks.reduce(
    (s, b) => s + Math.min(b.ads?.length ?? 0, MAX_ADS_SHOWN),
    0,
  );
  const angles = brief?.differentiation_angles ?? [];

  // Ad Strategist is tablet + desktop only — competitor analysis, the brief,
  // and the concept grid can't lay out on a phone. Gate below md (768px).
  if (isMobile) {
    return (
      <AppLayoutV2>
        <div className="min-h-[calc(100dvh-4rem)] flex flex-col items-center justify-center px-8 py-12 text-center">
          <i className="ri-computer-line text-5xl text-primary/40 mb-4" />
          <h1 className="text-lg font-bold text-dark mb-1.5">Ad Strategist needs a bigger screen</h1>
          <p className="text-sm text-dark-muted max-w-xs leading-relaxed mb-5">
            Competitor analysis, the brief, and the concept grid are built for
            tablet &amp; desktop. Open this on a larger screen to use it.
          </p>
          <button
            type="button"
            onClick={() => navigate('/create')}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-white text-sm font-bold hover:bg-primary-hover transition-colors cursor-pointer shadow-md shadow-primary/25"
          >
            <i className="ri-arrow-left-line" />
            Back to create
          </button>
        </div>
      </AppLayoutV2>
    );
  }

  // ─────────────────────────── Landing view ───────────────────────────
  if (view === 'list') {
    const STEPS = [
      { icon: 'ri-image-line', title: 'Pull competitor ads', body: "Fetch your competitors' best-performing live ads from the Meta Ad Library." },
      { icon: 'ri-eye-line', title: 'Find the openings', body: 'A vision model reads their creatives and spots the visual white space you can own.' },
      { icon: 'ri-shopping-bag-3-line', title: 'Pick your products', body: 'Choose which products to feature — we fan them across 20 concepts.' },
      { icon: 'ri-magic-line', title: 'Generate on demand', body: 'Render any concept into a finished, on-brand ad — one credit each.' },
    ];
    return (
      <AppLayoutV2>
        <div className="max-w-[1500px] mx-auto px-6 py-8">
          <h1 className="font-display text-2xl font-bold text-dark flex items-center gap-2">
            <i className="ri-radar-line text-primary" />
            Ad Strategist
            <span className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-wide">
              <i className="ri-vip-crown-2-line" /> Business
            </span>
          </h1>

          {/* Explainer hero — what this section actually does + the single CTA
              to start. (Previous analyses live below it.) */}
          <div className="mt-4 rounded-2xl border border-primary/20 bg-gradient-to-br from-primary-light/50 via-white to-accent-light/30 p-6">
            <div>
              <h2 className="font-display text-xl font-bold text-dark">
                Reverse-engineer your competitors&rsquo; winning ads
              </h2>
              <p className="text-sm text-dark-muted mt-1.5 leading-relaxed">
                Ad Strategist studies the ads your competitors are actually running, finds the
                angles and visual space they&rsquo;re missing, and turns that into finished,
                on-brand ad creatives you can generate in one click.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
              {STEPS.map((s, i) => (
                <div key={i} className="rounded-xl bg-white/70 border border-white p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-6 h-6 rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center">
                      {i + 1}
                    </span>
                    <i className={`${s.icon} text-primary`} />
                    <p className="font-display font-bold text-dark text-xs">{s.title}</p>
                  </div>
                  <p className="text-[11px] text-dark-muted leading-snug">{s.body}</p>
                </div>
              ))}
            </div>
            <div className="flex justify-center mt-5">
              <button
                type="button"
                onClick={startNewAnalysis}
                className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold cursor-pointer shadow-lg shadow-primary/25"
              >
                <i className="ri-sparkling-2-line text-base" /> New analysis
              </button>
            </div>
          </div>

          {error && (
            <div className="mt-4 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
              {error}
            </div>
          )}

          {runs === null ? (
            <div className="flex items-center gap-3 px-4 py-6 text-dark-muted">
              <i className="ri-loader-4-line animate-spin text-lg" /> Loading your analyses&hellip;
            </div>
          ) : runs.length === 0 ? (
            <p className="text-sm text-dark-muted mt-6 px-1">
              No analyses yet — hit <strong>New analysis</strong> above to run your first one.
            </p>
          ) : (
            <div className="mt-6 space-y-2">
              <p className="text-xs font-bold uppercase tracking-wide text-dark-muted mb-2">
                Previous analyses
              </p>
              {runs.map((run) => {
                const ready = run.status === 'ready';
                const failed = run.status === 'failed';
                const opening = openingRunId === run.id;
                const busy = rowBusyId === run.id;
                const renaming = renamingId === run.id;
                return (
                  <div
                    key={run.id}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white border border-surface-dark/40 hover:border-primary/40 hover:shadow-sm transition-all"
                  >
                    <div className="w-10 h-10 rounded-xl bg-primary-light/50 flex items-center justify-center text-primary shrink-0">
                      <i className="ri-file-chart-line" />
                    </div>

                    {renaming ? (
                      <div className="flex-1 flex items-center gap-2 min-w-0">
                        <input
                          autoFocus
                          value={renameValue}
                          onChange={(e) => setRenameValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') void saveRename(run.id);
                            if (e.key === 'Escape') setRenamingId(null);
                          }}
                          placeholder={runLabel(run)}
                          className="flex-1 min-w-0 px-3 py-1.5 rounded-lg border border-primary/50 text-sm outline-none"
                        />
                        <button
                          type="button"
                          onClick={() => saveRename(run.id)}
                          disabled={busy}
                          className="px-3 py-1.5 rounded-full bg-primary hover:bg-primary-hover text-white text-xs font-bold cursor-pointer disabled:opacity-50"
                        >
                          {busy ? <i className="ri-loader-4-line animate-spin" /> : 'Save'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setRenamingId(null)}
                          className="px-3 py-1.5 rounded-full border border-surface-dark/40 text-dark-muted text-xs font-bold cursor-pointer hover:bg-surface"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => openRun(run.id)}
                          disabled={opening || failed}
                          className="flex-1 min-w-0 text-left cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <p className="font-display font-bold text-dark text-sm truncate">
                            {runLabel(run)}
                          </p>
                          <p className="text-xs text-dark-muted">{fmtDate(run.created_at)}</p>
                        </button>
                        <span
                          className={`shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold ${
                            ready
                              ? 'bg-emerald-100 text-emerald-800'
                              : failed
                                ? 'bg-red-100 text-red-700'
                                : 'bg-amber-100 text-amber-800'
                          }`}
                        >
                          {ready ? 'Ready' : failed ? 'Failed' : 'Working'}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            setRenameValue(run.name || '');
                            setRenamingId(run.id);
                          }}
                          title="Rename"
                          className="shrink-0 w-8 h-8 rounded-full text-dark-muted hover:text-primary hover:bg-primary-light/50 flex items-center justify-center cursor-pointer transition-colors"
                        >
                          <i className="ri-pencil-line" />
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteRun(run.id)}
                          disabled={busy}
                          title="Delete"
                          className="shrink-0 w-8 h-8 rounded-full text-dark-muted hover:text-red-600 hover:bg-red-50 flex items-center justify-center cursor-pointer transition-colors disabled:opacity-50"
                        >
                          <i className={busy ? 'ri-loader-4-line animate-spin' : 'ri-delete-bin-line'} />
                        </button>
                        <button
                          type="button"
                          onClick={() => openRun(run.id)}
                          disabled={opening || failed}
                          className="shrink-0 w-8 h-8 rounded-full text-dark-muted hover:text-primary flex items-center justify-center cursor-pointer disabled:cursor-not-allowed"
                          aria-label="Open"
                        >
                          <i className={opening ? 'ri-loader-4-line animate-spin' : 'ri-arrow-right-s-line'} />
                        </button>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </AppLayoutV2>
    );
  }

  // ─────────────────────────── Analysis view ───────────────────────────
  return (
    <AppLayoutV2>
      <div className="max-w-[1500px] mx-auto px-6 py-8">
        <div className="mb-6">
          <button
            type="button"
            onClick={backToList}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-dark-muted hover:text-primary mb-3 cursor-pointer"
          >
            <i className="ri-arrow-left-line" /> All analyses
          </button>
          <h1 className="font-display text-2xl font-bold text-dark flex items-center gap-2">
            <i className="ri-radar-line text-primary" />
            Ad Strategist
          </h1>
          <p className="text-sm text-dark-muted mt-1">
            Competitor ads &rarr; visual openings &rarr; on-brand concepts, rendered the fast,
            reliable way.
          </p>
        </div>

        {error && (
          <div className="mb-4 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Step 1 — Competitor ads */}
        <AccordionStep
          step={1}
          title="Competitor ads"
          icon="ri-image-line"
          description="Your competitors' best-performing live ads, ranked by how long they've been running."
          state={stepState(1)}
          summary={
            competitorBlocks.length
              ? `${totalAds} ads across ${competitorBlocks.length} competitor${competitorBlocks.length === 1 ? '' : 's'}`
              : undefined
          }
          onEdit={() => setActiveStep(1)}
          cta={
            stepState(1) === 'active' && !running && brief ? (
              <button
                type="button"
                onClick={() => setActiveStep(2)}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold cursor-pointer"
              >
                See the openings <i className="ri-arrow-right-line" />
              </button>
            ) : undefined
          }
        >
          {running ? (
            <div className="flex items-center gap-3 px-4 py-4 rounded-xl bg-primary-light/40 border border-primary/20">
              <i className="ri-loader-4-line animate-spin text-primary text-lg" />
              <div>
                <p className="font-display font-bold text-dark text-sm">
                  Pulling competitor ads from the ad library&hellip;
                </p>
                <p className="text-xs text-dark-muted">This can take a minute or two.</p>
              </div>
            </div>
          ) : competitorBlocks.length === 0 ? (
            <p className="text-sm text-dark-muted">
              No competitor ads found. Add competitors in{' '}
              <a href="/competitors" className="text-primary font-semibold hover:underline">
                My Competitors
              </a>{' '}
              for a richer brief.
            </p>
          ) : (
            <div className="space-y-4">
              {competitorBlocks.map((b) => (
                <CompetitorAdStrip
                  key={b.query}
                  block={b}
                  onOpenAd={(ad) => setOpenAd({ ad, advertiser: b.label })}
                />
              ))}
            </div>
          )}
        </AccordionStep>

        {/* Step 2 — Strategic angles */}
        <AccordionStep
          step={2}
          title="Strategic angles"
          icon="ri-lightbulb-line"
          description="Where competitors aren't showing up — and how to take advantage."
          state={stepState(2)}
          summary={angles.length ? `${angles.length} angles identified` : undefined}
          onEdit={() => setActiveStep(2)}
          cta={
            stepState(2) === 'active' ? (
              <button
                type="button"
                onClick={() => setActiveStep(3)}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold cursor-pointer"
              >
                Pick products to feature <i className="ri-arrow-right-line" />
              </button>
            ) : undefined
          }
        >
          <div className="space-y-4 text-sm text-dark-muted leading-relaxed">
            {brief?.brand_snapshot && (
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-dark mb-1">
                  Your brand right now
                </p>
                <p>{brief.brand_snapshot}</p>
              </div>
            )}
            {brief?.visual_gap && (
              <div className="rounded-xl bg-accent/5 border border-accent/25 px-4 py-3">
                <p className="text-xs font-bold uppercase tracking-wide text-accent mb-1 flex items-center gap-1.5">
                  <i className="ri-eye-line" /> How to stand out visually
                </p>
                <p className="text-dark">{brief.visual_gap}</p>
                <p className="text-[11px] text-dark-muted/70 mt-1.5">
                  Read from your competitors&rsquo; actual top-performing ad creatives.
                </p>
              </div>
            )}
            {brief?.gap_analysis && (
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-dark mb-1">
                  Where the competition isn&rsquo;t showing up
                </p>
                <p>{brief.gap_analysis}</p>
              </div>
            )}
            {angles.length > 0 && (
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-dark mb-2">
                  Angles you can own
                </p>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  {angles.map((a, i) => (
                    <div
                      key={i}
                      className="rounded-xl bg-primary-light/30 border border-primary/15 px-4 py-3"
                    >
                      <p className="font-display font-bold text-primary text-sm mb-1">{a.angle}</p>
                      <p className="text-xs text-dark-muted leading-snug">{a.rationale}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </AccordionStep>

        {/* Step 3 — Pick products */}
        <AccordionStep
          step={3}
          title="Pick products to feature"
          icon="ri-shopping-bag-3-line"
          description="The strategist will fan your selection across 20 unique ad concepts."
          state={stepState(3)}
          onEdit={() => setActiveStep(3)}
          cta={
            stepState(3) === 'active' ? (
              <button
                type="button"
                onClick={buildConcepts}
                disabled={buildingConcepts}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i
                  className={
                    buildingConcepts ? 'ri-loader-4-line animate-spin' : 'ri-sparkling-2-line'
                  }
                />
                {buildingConcepts ? 'Writing your winning ideas…' : 'Generate winning ideas'}
              </button>
            ) : undefined
          }
        >
          <ProductCatalogPicker onSelectionChange={setSelection} />
        </AccordionStep>

        {/* Step 4 — Winning ideas grid */}
        <AccordionStep
          step={4}
          title="Winning ideas"
          icon="ri-magic-line"
          description="Click a tile to generate that ad — or generate them all at once."
          state={stepState(4)}
        >
          {!ideas ? (
            <p className="text-sm text-dark-muted">Pick products above to build your winning ideas.</p>
          ) : (
            <div>
              <div className="flex items-center justify-between gap-4 flex-wrap mb-5">
                <p className="text-xs text-dark-muted">
                  {ideas.reduce((s, i) => s + (i.concepts?.length ?? 0), 0)} concepts · 1 credit each ·
                  you have <span className="font-bold text-dark">{balance ?? 0}</span> credits
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => navigate('/gallery')}
                    className="px-4 py-2 rounded-full border border-surface-dark/40 text-dark text-xs font-bold hover:bg-surface cursor-pointer"
                  >
                    View in My Creatives
                  </button>
                  <button
                    type="button"
                    onClick={generateAll}
                    disabled={generatingAll}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold cursor-pointer shadow-lg shadow-primary/25 disabled:opacity-50"
                  >
                    <i
                      className={`text-base ${generatingAll ? 'ri-loader-4-line animate-spin' : 'ri-sparkling-2-line'}`}
                    />
                    {generatingAll ? 'Generating…' : 'Generate All'}
                  </button>
                </div>
              </div>

              {ideas.map((idea, ii) => (
                <section key={ii} className="mb-8">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-6 h-6 rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center">
                      {ii + 1}
                    </span>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wide text-primary">
                        Strategic Angle
                      </p>
                      <h3 className="font-display font-bold text-dark text-lg leading-tight">
                        {idea.angle_name}
                      </h3>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
                    {(idea.concepts || []).map((concept, ci) => (
                      <ConceptTile
                        key={ci}
                        concept={concept}
                        product={productsById.get(concept.recommended_product_id)}
                        slot={slots[key(ii, ci)]}
                        onGenerate={() => renderOne(ii, ci)}
                        onEdit={(hl, sub) => editConcept(ii, ci, hl, sub)}
                        disabled={generatingAll}
                      />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </AccordionStep>
      </div>

      {openAd && (
        <CompetitorAdModal
          ad={openAd.ad}
          advertiser={openAd.advertiser}
          onClose={() => setOpenAd(null)}
        />
      )}
    </AppLayoutV2>
  );
}

// ── Single concept tile — idle → Generate button, pending/queued → loader,
// rendered → thumbnail (click → My Creatives). ──
function ConceptTile({
  concept,
  product,
  slot,
  onGenerate,
  onEdit,
  disabled,
}: {
  concept: AdStrategist20Concept;
  product: UserProduct | undefined;
  slot: SlotState | undefined;
  onGenerate: () => void;
  onEdit: (headline: string, subhead: string) => void | Promise<void>;
  disabled: boolean;
}) {
  const palette = hookChip(concept.hook_type);
  const status = slot?.status ?? 'idle';
  const rendered = slot?.status === 'rendered' ? slot : null;

  // Inline copy edit (pencil → editable headline/subhead → Save/Cancel).
  const [editing, setEditing] = useState(false);
  const [hl, setHl] = useState(concept.headline || '');
  const [sub, setSub] = useState(concept.subhead || '');
  const [saving, setSaving] = useState(false);

  return (
    <div className="group flex flex-col rounded-2xl bg-white border border-surface-dark/40 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      <div
        className="relative aspect-square flex items-center justify-center px-3 py-5"
        style={{
          background: rendered
            ? '#f5f5fa'
            : `linear-gradient(135deg, ${palette.hex}18 0%, ${palette.hex}05 100%)`,
        }}
      >
        {rendered && rendered.imageUrl ? (
          <a
            href={`/creatives/${rendered.postId}`}
            className="absolute inset-0 block group/thumb"
            title="Open in My Creatives →"
          >
            <img
              src={rendered.imageUrl}
              alt={concept.headline}
              className="w-full h-full object-contain"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-black/0 group-hover/thumb:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover/thumb:opacity-100">
              <span className="px-3 py-1.5 rounded-full bg-white text-dark text-xs font-bold inline-flex items-center gap-1.5">
                <i className="ri-arrow-right-line" />
                Open in My Creatives
              </span>
            </div>
            <span
              className={`${palette.chip} absolute bottom-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide shadow-sm`}
            >
              <i className="ri-flashlight-line text-[10px]" />
              {concept.hook_type.replace(/-/g, ' ')}
            </span>
          </a>
        ) : (
          <div className="flex flex-col items-center gap-2 w-full">
            <span
              className={`${palette.chip} inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide`}
            >
              <i className="ri-flashlight-line text-[10px]" />
              {concept.hook_type.replace(/-/g, ' ')}
            </span>
            {status === 'idle' && (
              <button
                type="button"
                onClick={onGenerate}
                disabled={disabled}
                className="mt-1 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary hover:bg-primary-hover text-white text-xs font-bold shadow-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              >
                <i className="ri-sparkling-2-line" />
                Generate <span className="opacity-70">(1cr)</span>
              </button>
            )}
            {(status === 'pending' ||
              status === 'queued' ||
              (status === 'rendered' && !rendered?.imageUrl)) && (
              <div className="mt-1 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-surface-dark/30 text-dark-muted text-xs font-bold">
                <i className="ri-loader-4-line animate-spin" />
                {status === 'pending' ? 'Queueing…' : 'Rendering…'}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="p-3 flex flex-col gap-1.5 flex-1">
        {editing ? (
          <div className="flex flex-col gap-2">
            <textarea
              value={hl}
              onChange={(e) => setHl(e.target.value)}
              rows={2}
              placeholder="Headline"
              className="w-full rounded-lg border border-surface-dark/40 px-2 py-1.5 text-sm font-display font-bold text-dark leading-snug resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
            <textarea
              value={sub}
              onChange={(e) => setSub(e.target.value)}
              rows={3}
              placeholder="Subhead"
              className="w-full rounded-lg border border-surface-dark/40 px-2 py-1.5 text-xs text-dark-muted leading-snug resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={!hl.trim() || saving}
                onClick={async () => {
                  setSaving(true);
                  try {
                    await onEdit(hl.trim(), sub.trim());
                    setEditing(false);
                  } finally {
                    setSaving(false);
                  }
                }}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary hover:bg-primary-hover text-white text-[11px] font-bold disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              >
                {saving ? (
                  <i className="ri-loader-4-line animate-spin" />
                ) : (
                  <i className="ri-check-line" />
                )}
                Save
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={() => {
                  setHl(concept.headline || '');
                  setSub(concept.subhead || '');
                  setEditing(false);
                }}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-surface-dark/30 text-dark-muted text-[11px] font-bold disabled:opacity-40 cursor-pointer"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="relative">
            <button
              type="button"
              onClick={() => {
                setHl(concept.headline || '');
                setSub(concept.subhead || '');
                setEditing(true);
              }}
              title="Edit copy"
              className="absolute -top-0.5 right-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold text-dark-muted/70 hover:text-primary hover:bg-primary/5 transition-colors cursor-pointer"
            >
              <i className="ri-pencil-line" />
              Edit
            </button>
            <h4 className="font-display font-bold text-dark text-sm leading-snug pr-12">
              {concept.headline}
            </h4>
            <p className="text-xs text-dark-muted leading-snug mt-1.5">{concept.subhead}</p>
          </div>
        )}
        {concept.visual_direction && (
          <p className="pt-1.5 text-[11px] text-dark-muted/80 italic leading-snug flex items-start gap-1">
            <i className="ri-image-line mt-0.5 shrink-0" />
            {concept.visual_direction}
          </p>
        )}
        {/* Featured product — matches old AS: thumb + PRODUCT label + name. */}
        <div className="mt-auto pt-2 border-t border-surface-dark/20 flex items-center gap-2">
          {product?.imageUrl && (
            <div className="w-12 h-12 rounded-lg overflow-hidden bg-surface/40 flex-shrink-0 border border-surface-dark/30">
              <img
                src={product.imageUrl}
                alt=""
                className="w-full h-full object-contain p-1"
                loading="lazy"
              />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-[10px] text-dark-muted/70 uppercase tracking-wide">Product</p>
            <p className="text-xs font-bold text-dark line-clamp-2 leading-tight">
              {product?.name || '(unknown product)'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
